import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { equipment as equipmentApi } from '../../services/api';
import EquipmentEditor from './EquipmentEditor';
import { Chip, SourceChips, Switch, Thumb, errorList, useDebounced } from './shared';

/*
 * O separador "Equipamento": as máquinas do ginásio e, sobretudo, se o ginásio as TEM. O catálogo
 * traz as máquinas mais comuns, todas marcadas como existentes; o ginásio acerta-o à sua realidade —
 * uma a uma, ou várias de uma vez — e os exercícios que dependem do que não tem deixam de ser
 * sugeridos como alternativa.
 */

const PAGE_SIZE = 24;
const INITIAL_FILTERS = { q: '', kind: '', family: '', source: '', availability: '', hasMedia: '', includeInactive: false };

const toParams = (f, page) => {
  const p = { page, pageSize: PAGE_SIZE };
  if (f.q.trim()) p.q = f.q.trim();
  if (f.kind) p.kind = f.kind;
  if (f.family) p.family = f.family;
  if (f.source) p.source = f.source;
  if (f.availability === 'yes') p.available = true;
  if (f.availability === 'no') p.available = false;
  if (f.hasMedia) p.hasMedia = f.hasMedia === 'true';
  if (f.includeInactive) p.includeInactive = true;
  return p;
};

export default function EquipmentPanel({ meta, onChanged, onCountChange }) {
  const [filters, setFilters] = useState(INITIAL_FILTERS);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [page, setPage] = useState(1);
  const [result, setResult] = useState({ items: [], total: 0, page: 1, pageSize: PAGE_SIZE });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editing, setEditing] = useState(undefined);
  const [selected, setSelected] = useState(() => new Set());
  const [busy, setBusy] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const requestRef = useRef(0);
  const callbacks = useRef({ onChanged, onCountChange });
  callbacks.current = { onChanged, onCountChange };

  const debouncedQuery = useDebounced(filters.q, 300);
  const effective = useMemo(() => ({ ...filters, q: debouncedQuery }), [filters, debouncedQuery]);

  useEffect(() => { setPage(1); setSelected(new Set()); }, [effective]);

  useEffect(() => {
    const request = ++requestRef.current;
    setLoading(true);
    equipmentApi.search(toParams(effective, page))
      .then(({ data }) => {
        if (request !== requestRef.current) return;
        setResult(data);
        setError(null);
        callbacks.current.onCountChange?.(data.total, effective);
      })
      .catch((e) => { if (request === requestRef.current) setError(errorList(e, 'Não foi possível carregar o equipamento.').join(' ')); })
      .finally(() => { if (request === requestRef.current) setLoading(false); });
  }, [effective, page, reloadKey]);

  const set = (patch) => setFilters((f) => ({ ...f, ...patch }));
  const pages = Math.max(1, Math.ceil(result.total / result.pageSize));
  const filtered = JSON.stringify(filters) !== JSON.stringify(INITIAL_FILTERS);
  const changed = useCallback(() => { setReloadKey((k) => k + 1); callbacks.current.onChanged?.(); }, []);

  const toggleAvailability = async (item, available) => {
    // Optimista: o interruptor responde já; se o servidor recusar, volta atrás.
    const patchItem = (value) => setResult((r) => ({ ...r, items: r.items.map((i) => (i.id === item.id ? { ...i, isAvailable: value } : i)) }));
    patchItem(available);
    try {
      await equipmentApi.setAvailability(item.id, available);
      callbacks.current.onChanged?.();
    } catch (e) {
      patchItem(!available);
      toast.error(errorList(e).join(' '));
    }
  };

  const toggleSelected = (id) => setSelected((s) => {
    const next = new Set(s);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const allOnPage = result.items.length > 0 && result.items.every((i) => selected.has(i.id));
  const toggleAllOnPage = () => setSelected(allOnPage ? new Set() : new Set(result.items.map((i) => i.id)));

  const bulk = async (available) => {
    setBusy(true);
    try {
      const { data } = await equipmentApi.setAvailabilityBulk([...selected], available);
      toast.success(`${data.updated} ${data.updated === 1 ? 'máquina marcada' : 'máquinas marcadas'} como ${available ? 'existentes' : 'inexistentes'}`);
      setSelected(new Set());
      changed();
    } catch (e) {
      toast.error(errorList(e).join(' '));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <div className="lib-filters">
        <div className="lib-filters-top">
          <input className="lib-input" type="search" value={filters.q} onChange={(e) => set({ q: e.target.value })}
            placeholder="Procurar máquina ou equipamento…" aria-label="Procurar equipamento" />
          <button type="button" className="lib-btn lib-filter-toggle" aria-expanded={filtersOpen} onClick={() => setFiltersOpen((o) => !o)}>Filtros</button>
          {meta.canManage && <button type="button" className="lib-btn primary" onClick={() => setEditing(null)}>+ Nova</button>}
        </div>
        <div className={`lib-filter-grid${filtersOpen ? ' open' : ''}`}>
          <select className="lib-select" aria-label="Tipo" value={filters.kind} onChange={(e) => set({ kind: e.target.value })}>
            <option value="">Todos os tipos</option>
            {meta.equipmentKinds.map((k) => <option key={k.code} value={k.code}>{k.label}</option>)}
          </select>
          <select className="lib-select" aria-label="Zona do ginásio" value={filters.family} onChange={(e) => set({ family: e.target.value })}>
            <option value="">Todas as zonas</option>
            {meta.equipmentFamilies.map((k) => <option key={k.code} value={k.code}>{k.label}</option>)}
          </select>
          <select className="lib-select" aria-label="Disponibilidade" value={filters.availability} onChange={(e) => set({ availability: e.target.value })}>
            <option value="">Temos ou não temos</option>
            <option value="yes">O ginásio tem</option>
            <option value="no">O ginásio não tem</option>
          </select>
          <select className="lib-select" aria-label="Origem" value={filters.source} onChange={(e) => set({ source: e.target.value })}>
            <option value="">Catálogo e do ginásio</option>
            <option value="catalog">Só do catálogo</option>
            <option value="custom">Só do ginásio</option>
          </select>
          <select className="lib-select" aria-label="Imagem" value={filters.hasMedia} onChange={(e) => set({ hasMedia: e.target.value })}>
            <option value="">Com ou sem imagem</option>
            <option value="true">Com imagem</option>
            <option value="false">Sem imagem</option>
          </select>
          <div className="lib-filter-checks">
            {meta.canManage && <label className="lib-check"><input type="checkbox" checked={filters.includeInactive} onChange={(e) => set({ includeInactive: e.target.checked })} />Incluir desativados</label>}
            {filtered && <button type="button" className="lib-btn small ghost" onClick={() => setFilters(INITIAL_FILTERS)}>Limpar filtros</button>}
          </div>
        </div>
      </div>

      {error && <div className="lib-notice bad" role="alert">{error}</div>}

      {meta.canManage && result.items.length > 0 && (
        <label className="lib-check" style={{ marginBottom: 8 }}>
          <input type="checkbox" checked={allOnPage} onChange={toggleAllOnPage} />
          Selecionar as {result.items.length} desta página (para marcar várias de uma vez)
        </label>
      )}

      <div aria-live="polite">
        {loading && result.items.length === 0 && <div className="lib-loading" role="status">A carregar…</div>}
        {!loading && !error && result.items.length === 0 && (
          <div className="lib-empty">{filtered ? 'Nenhum equipamento com estes filtros.' : 'Ainda não há equipamento.'}</div>
        )}
      </div>

      <div className="lib-list equipment" style={{ opacity: loading && result.items.length > 0 ? 0.6 : 1 }}>
        {result.items.map((item) => (
          <div key={item.id} className={`lib-card${item.isActive ? '' : ' inactive'}${selected.has(item.id) ? ' selected' : ''}`}
            style={{ cursor: 'default' }}>
            <Thumb src={item.imageUrl} icon="⚙️" />
            <div className="lib-card-body">
              <button type="button" className="lib-card-title" onClick={() => setEditing(item.id)}
                style={{ appearance: 'none', background: 'none', border: 'none', padding: 0, font: 'inherit', fontWeight: 700, textAlign: 'left', cursor: 'pointer', color: 'inherit' }}>
                {item.name}
              </button>
              {item.nameEn && <span className="lib-card-sub">{item.nameEn}</span>}
              <span className="lib-chips">
                <Chip tone="accent">{item.kindLabel}</Chip>
                {item.familyLabel && <Chip>{item.familyLabel}</Chip>}
                <SourceChips item={item} />
              </span>
              <span className="lib-card-meta">{item.exerciseCount} {item.exerciseCount === 1 ? 'exercício' : 'exercícios'}</span>
            </div>
            <div className="lib-card-actions">
              <Switch checked={item.isAvailable} disabled={!meta.canManage} onChange={(v) => toggleAvailability(item, v)}
                onLabel="Temos" offLabel="Não temos" label={`O ginásio tem: ${item.name}`} />
              {meta.canManage && (
                <label className="lib-check" title="Selecionar para marcar várias de uma vez">
                  <input type="checkbox" checked={selected.has(item.id)} onChange={() => toggleSelected(item.id)} aria-label={`Selecionar ${item.name}`} />
                </label>
              )}
            </div>
          </div>
        ))}
      </div>

      {result.total > 0 && (
        <div className="lib-pager">
          <span>{result.total} {result.total === 1 ? 'máquina' : 'máquinas'} · página {result.page} de {pages}</span>
          <span style={{ display: 'flex', gap: 8 }}>
            <button type="button" className="lib-btn small" disabled={page <= 1 || loading} onClick={() => setPage((p) => p - 1)}>Anterior</button>
            <button type="button" className="lib-btn small" disabled={page >= pages || loading} onClick={() => setPage((p) => p + 1)}>Seguinte</button>
          </span>
        </div>
      )}

      {selected.size > 0 && (
        <div className="lib-bulkbar" role="region" aria-label="Ações em bloco">
          <strong>{selected.size} {selected.size === 1 ? 'selecionada' : 'selecionadas'}</strong>
          <span style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button type="button" className="lib-btn small" disabled={busy} onClick={() => bulk(true)}>O ginásio tem</button>
            <button type="button" className="lib-btn small" disabled={busy} onClick={() => bulk(false)}>O ginásio não tem</button>
            <button type="button" className="lib-btn small ghost" disabled={busy} onClick={() => setSelected(new Set())}>Limpar seleção</button>
          </span>
        </div>
      )}

      {editing !== undefined && (
        <EquipmentEditor
          key={editing ?? 'new'}
          equipmentId={editing}
          meta={meta}
          onClose={() => { setEditing(undefined); changed(); }}
          onSaved={() => setReloadKey((k) => k + 1)}
          onDeleted={() => { setEditing(undefined); changed(); }}
        />
      )}
    </div>
  );
}
